import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Check } from "lucide-react";
import "./TeamSocialDimsumAvailabilityPage.css";

import progressIcon from "../../../assets/progressIcon4.png";
import nextIcon from "../../../assets/nextButtonIcon.png";

import MobileTopBar from "../../../components/MobileTopBar";
import MobileBottomBar from "../../../components/MobileBottomBar";
import DesktopTopBar from "../../../components/DesktopTopBar";
import DesktopBottomBar from "../../../components/DesktopBottomBar";

import { useGlobalAlert } from "../../../context/AlertContext";

const TeamSocialDimsumAvailabilityPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerAlert } = useGlobalAlert();

  const isLoading = false;
  const dogName = formData.dogName || "Dog";
  const formattedDogName =
    dogName.charAt(0).toUpperCase() + dogName.slice(1).toLowerCase();

  const pastimeOptions = [
    "🚶 Walks & exploring",
    "🌲 Hiking & adventures",
    "🏃 Running & fitness",
    "🎾 Playing & fetch",
    "🐕 Dog parks & playdates",
    "🏖️ Beach & swimming",
    "🚗 Car rides & day trips",
    "☕ Cafés & hanging out",
    "🛋️ Cuddling & relaxing",
  ];

  useEffect(() => {
    const existingHobbies =
      formData.selectedHobbies || formData.dogHobbies || formData.hobbies;
    if (existingHobbies && Array.isArray(existingHobbies)) {
      const custom = existingHobbies.find((h) => !pastimeOptions.includes(h));
      setFormData((prev) => ({
        ...prev,
        selectedHobbies: existingHobbies,
        dogHobbies: existingHobbies,
        customPastime: custom || prev.customPastime || "",
      }));
    }
  }, []);

  const toggleItem = (item, field) => {
    const currentList = formData[field] || [];
    const isSelected = currentList.includes(item);

    // Limit to 3 choices
    if (!isSelected && currentList.length >= 3) {
      if (triggerAlert) {
        triggerAlert("You can only select up to 3 hobbies.", "warning");
      }
      return;
    }

    const newList = isSelected
      ? currentList.filter((i) => i !== item)
      : [...currentList, item];

    setFormData((prev) => ({
      ...prev,
      [field]: newList,
      dogHobbies: newList,
    }));
  };

  const handleCustomWriteOwn = (value, listField, prevValueField) => {
    const currentList = formData[listField] || [];
    const oldValue = formData[prevValueField] || "";

    let updatedList = currentList.filter((item) => item !== oldValue);

    if (value.trim() !== "") {
      // Limit custom input addition to the max 3 rule
      if (!oldValue && updatedList.length >= 3) {
        if (triggerAlert) {
          triggerAlert("You can only select up to 3 hobbies.", "warning");
        }
        return;
      }
      updatedList = [...updatedList, value];
    }

    setFormData((prev) => ({
      ...prev,
      [listField]: updatedList,
      dogHobbies: updatedList,
      [prevValueField]: value,
    }));
  };

  const hasHobbies =
    (formData.selectedHobbies || []).length >= 1 ||
    (formData.customPastime && formData.customPastime.trim().length > 0);

  const handleNext = () => {
    if (hasHobbies) navigate("/team-social-bio");
  };

  return (
    <div className="tsda-container">
      <div className="tsda-panel tsda-main-panel">
        {/* Mobile Top Bar */}
        <MobileTopBar
          onBack={() => navigate("/team-social-experience")}
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        {/* Desktop Top Bar */}
        <div className="tsda-desktop-top-bar-wrapper tsda-desktop-only">
          <DesktopTopBar
            showBack={true}
            onBack={() => navigate("/team-social-experience")}
            backColor="#fff"
            showProgress={false}
          />
        </div>

        <div className="tsda-content-wrapper tsda-right-content">
          <div className="tsda-header-section tsda-mobile-only">
            <div className="tsda-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress Step"
                className="tsda-progress-icon"
              />
            </div>
            <div className="tsda-progress-track">
              <div className="tsda-progress-fill tsda-step-2-fill"></div>
            </div>
          </div>

          <div className="tsda-text-group">
            <h1
              className="tsda-title tsda-text-white"
              style={{ textAlign: "center" }}
            >
              {/* WHAT ARE {dogName.toUpperCase()}'S TOP 3 HOBBIES? */}
              What are your top 3 dog-friendly activities?
            </h1>
            <p
              className="tsda-subtitle tsda-text-white-sub"
              style={{ marginBottom: "1rem" }}
            >
              {/* We'll use your choices to help match your pup with Pup Pals who
              share their interests! */}
              We'll use your choices to help match you with Pups who share your interests!
            </p>
            <p
              className="tsda-subtitle tsda-text-white-sub"
              style={{ textAlign: "center", marginBottom: "1.5rem" }}
            >
              Choose up to 3.
            </p>
          </div>

          <div className="tsda-list-container">
            {pastimeOptions.map((pastime, index) => {
              const isChecked = (formData.selectedHobbies || []).includes(
                pastime,
              );
              return (
                <div
                  key={index}
                  className="tsda-option-row tsda-border-light"
                  onClick={() => toggleItem(pastime, "selectedHobbies")}
                >
                  <span className="tsda-option-label tsda-text-white">
                    {pastime}
                  </span>
                  <div
                    className={`tsda-checkbox ${isChecked ? "checked" : ""}`}
                  >
                    {isChecked && (
                      <Check size={18} strokeWidth={4} color="#000" />
                    )}
                  </div>
                </div>
              );
            })}

            <div
              className="tsda-write-own-box tsda-bg-light-blue"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingRight: "15px",
              }}
            >
              <input
                type="text"
                placeholder="Other: ______"
                className="tsda-write-own-input"
                value={formData.customPastime || ""}
                onChange={(e) =>
                  handleCustomWriteOwn(
                    e.target.value,
                    "selectedHobbies",
                    "customPastime",
                  )
                }
              />
              {formData.customPastime?.trim() && (
                <div className="tsda-checkbox checked">
                  <Check size={18} strokeWidth={4} color="#000" />
                </div>
              )}
            </div>
          </div>

          <DesktopBottomBar
            onNext={handleNext}
            isNextEnabled={hasHobbies}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={() => navigate("/team-social-bio")}
            isLoading={isLoading}
            variant="green"
          />

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={hasHobbies}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={() => navigate("/team-social-bio")}
            isLoading={isLoading}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default TeamSocialDimsumAvailabilityPage;
